import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';

const SUBMIT_FORM_URL = 'https://readdy.ai/api/form/d8oi2tfij7vns5otivn0';
const ISSUE_FORM_URL = 'https://readdy.ai/api/form/d8oj6pnij7vns5otivvg';

interface FileState {
  file: File | null;
  label: string;
}

interface SubmitFormData {
  requester_name: string;
  requester_phone: string;
  patient_name: string;
  patient_birth: string;
  insurance_company: string;
  notes: string;
}

interface IssueFormData {
  requester_name: string;
  requester_phone: string;
  patient_name: string;
  patient_birth: string;
  insurance_company: string;
  care_start_date: string;
  care_end_date: string;
  notes: string;
}

const initialSubmitForm: SubmitFormData = {
  requester_name: '',
  requester_phone: '',
  patient_name: '',
  patient_birth: '',
  insurance_company: '',
  notes: '',
};

const initialIssueForm: IssueFormData = {
  requester_name: '',
  requester_phone: '',
  patient_name: '',
  patient_birth: '',
  insurance_company: '',
  care_start_date: '',
  care_end_date: '',
  notes: '',
};

const docFileLabels = [
  { key: 'nurse_record', label: '간호기록지', source: '병원 발급', icon: 'ri-hospital-line' },
  { key: 'admission_discharge', label: '입·퇴원 확인서', source: '병원 발급', icon: 'ri-file-text-line' },
  { key: 'care_diary', label: '간병일지', source: '회사 양식', icon: 'ri-edit-box-line' },
  { key: 'transfer_receipt', label: '이체확인증', source: '직접 캡쳐', icon: 'ri-bank-card-line' },
];

const steps = [
  {
    phase: '1단계',
    timing: '간병 시작 전',
    icon: 'ri-hospital-line',
    title: '병원 사전확인',
    desc: '병원에 간호기록지 작성 가능 여부와 간병인 입출입 기록 가능 여부를 반드시 확인하세요.',
    items: ['간호기록지 작성 가능 여부 확인', '간병인 입출입 기록 가능 여부 확인', '담당 간호사 또는 원무과 문의'],
    warning: '보험금 지급을 위한 필수 사항입니다. 반드시 사전에 확인하세요!',
  },
  {
    phase: '2단계',
    timing: '간병 기간 중',
    icon: 'ri-edit-box-line',
    title: '간병일지 매일 작성',
    desc: '간병인은 코리아케어러스 공식 양식으로 간병 기간 동안 매일 간병일지를 작성해야 합니다.',
    items: ['코리아케어러스 공식 양식 사용', '간병인이 매일 직접 작성', '간병 시간·환자 상태·수행 업무 기록'],
  },
  {
    phase: '3단계',
    timing: '간병 종료 후',
    icon: 'ri-money-dollar-circle-line',
    title: '정산 및 증빙',
    desc: '간병비 정산 시 이체 메모와 이체확인증 캡쳐가 매우 중요합니다.',
    items: ['환자→간병인 계좌이체 (메모: 간병비 지급)', '이체확인증 캡쳐 보관', '회사 중개 수수료 별도 지급'],
    warning: '환자 이름과 간병인 이름이 계좌 정보와 일치해야 합니다.',
  },
  {
    phase: '4단계',
    timing: '정산 완료 후',
    icon: 'ri-bank-line',
    title: '서류 제출 및 보험청구',
    desc: '4종 서류를 당사에 제출하면 3종 서류를 발급해 드립니다. 전체 서류를 보험사에 청구하세요.',
    items: ['회사에 4종 서류 제출 (간호기록지·입퇴원확인서·간병일지·이체확인증)', '회사가 3종 발급 (이용내역·회원가입·사업자등록증)', '전체 서류 보험사에 제출'],
  },
];

export default function AppInsuranceDocPage() {
  const navigate = useNavigate();

  // Submit form state
  const [submitForm, setSubmitForm] = useState<SubmitFormData>(initialSubmitForm);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitDone, setSubmitDone] = useState(false);
  const [submitAgreed, setSubmitAgreed] = useState(false);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [files, setFiles] = useState<Record<string, FileState>>({
    nurse_record: { file: null, label: '간호기록지' },
    admission_discharge: { file: null, label: '입·퇴원 확인서' },
    care_diary: { file: null, label: '간병일지' },
    transfer_receipt: { file: null, label: '이체확인증' },
  });
  // Track which file cards are "expanded" (clicked open)
  const [activeFileCard, setActiveFileCard] = useState<string | null>(null);

  // Issue form state
  const [issueForm, setIssueForm] = useState<IssueFormData>(initialIssueForm);
  const [issueLoading, setIssueLoading] = useState(false);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [issueDone, setIssueDone] = useState(false);
  const [issueAgreed, setIssueAgreed] = useState(false);
  const [showIssueForm, setShowIssueForm] = useState(false);

  // Accordion state
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [showDownload, setShowDownload] = useState(false);

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Refs for scroll targets
  const submitSectionRef = useRef<HTMLDivElement>(null);
  const issueSectionRef = useRef<HTMLDivElement>(null);

  const handleFileChange = (key: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFiles((prev) => ({ ...prev, [key]: { ...prev[key], file } }));
    if (file) {
      setActiveFileCard(null); // close card after selection
    }
  };

  const handleSubmitChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSubmitForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleIssueChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setIssueForm((prev) => ({ ...prev, [name]: value }));
  };

  const scrollToSection = (ref: React.RefObject<HTMLDivElement | null>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!submitForm.requester_name.trim() || !submitForm.requester_phone.trim() || !submitForm.patient_name.trim() || !submitForm.patient_birth.trim() || !submitForm.insurance_company.trim()) {
      setSubmitError('필수 항목을 모두 입력해주세요.');
      return;
    }

    if (!submitAgreed) {
      setSubmitError('개인정보 수집 및 이용에 동의해주세요.');
      return;
    }

    setSubmitLoading(true);

    try {
      const params = new URLSearchParams();
      params.append('requester_name', submitForm.requester_name);
      params.append('requester_phone', submitForm.requester_phone);
      params.append('patient_name', submitForm.patient_name);
      params.append('patient_birth', submitForm.patient_birth);
      params.append('insurance_company', submitForm.insurance_company);
      params.append('notes', submitForm.notes);

      docFileLabels.forEach((d) => {
        const f = files[d.key];
        params.append(`file_${d.key}`, f.file ? f.file.name : '미첨부');
      });

      await fetch(SUBMIT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      setSubmitDone(true);
    } catch {
      setSubmitError('신청 중 오류가 발생했습니다. 다시 시도해주세요.');
    }

    setSubmitLoading(false);
  };

  const handleIssueForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setIssueError(null);

    if (!issueForm.requester_name.trim() || !issueForm.requester_phone.trim() || !issueForm.patient_name.trim() || !issueForm.patient_birth.trim() || !issueForm.insurance_company.trim()) {
      setIssueError('필수 항목을 모두 입력해주세요.');
      return;
    }

    if (!issueAgreed) {
      setIssueError('개인정보 수집 및 이용에 동의해주세요.');
      return;
    }

    setIssueLoading(true);

    try {
      const params = new URLSearchParams();
      params.append('requester_name', issueForm.requester_name);
      params.append('requester_phone', issueForm.requester_phone);
      params.append('patient_name', issueForm.patient_name);
      params.append('patient_birth', issueForm.patient_birth);
      params.append('insurance_company', issueForm.insurance_company);
      params.append('care_start_date', issueForm.care_start_date);
      params.append('care_end_date', issueForm.care_end_date);
      params.append('notes', issueForm.notes);

      await fetch(ISSUE_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      setIssueDone(true);
    } catch {
      setIssueError('신청 중 오류가 발생했습니다. 다시 시도해주세요.');
    }

    setIssueLoading(false);
  };

  const inputClass = 'w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#2E7D6B] focus:ring-1 focus:ring-[#2E7D6B] transition-colors bg-white placeholder-gray-300';
  const labelClass = 'block text-xs font-bold text-gray-600 mb-1.5';

  const uploadedCount = Object.values(files).filter((f) => f.file !== null).length;

  // Success screen for submit
  if (submitDone) {
    return (
      <AppLayout>
        <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-white">
          <div className="w-16 h-16 rounded-2xl bg-[#E8F5E9] flex items-center justify-center mb-5">
            <i className="ri-check-line text-3xl text-[#2E7D6B]" />
          </div>
          <h1 className="text-xl font-extrabold text-gray-900 mb-2">서류제출 완료!</h1>
          <p className="text-sm text-gray-500 text-center leading-relaxed mb-8">
            제출하신 서류가 정상적으로 접수되었습니다.<br />
            담당자가 확인 후 빠른 시일 내에 연락드리겠습니다.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/app')}
              className="px-6 py-3 rounded-xl text-sm font-bold text-[#2E7D6B] bg-[#E8F5E9] whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity"
            >
              대시보드로
            </button>
            <button
              onClick={() => {
                setSubmitDone(false);
                setSubmitForm(initialSubmitForm);
                setSubmitAgreed(false);
                setFiles({
                  nurse_record: { file: null, label: '간호기록지' },
                  admission_discharge: { file: null, label: '입·퇴원 확인서' },
                  care_diary: { file: null, label: '간병일지' },
                  transfer_receipt: { file: null, label: '이체확인증' },
                });
              }}
              className="px-6 py-3 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              추가 제출하기
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Success screen for issue
  if (issueDone) {
    return (
      <AppLayout>
        <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-white">
          <div className="w-16 h-16 rounded-2xl bg-[#E8F5E9] flex items-center justify-center mb-5">
            <i className="ri-check-line text-3xl text-[#2E7D6B]" />
          </div>
          <h1 className="text-xl font-extrabold text-gray-900 mb-2">서류 발급 요청 완료!</h1>
          <p className="text-sm text-gray-500 text-center leading-relaxed mb-8">
            서류 발급 요청이 정상적으로 접수되었습니다.<br />
            3종 서류 발급까지 영업일 기준 2~3일 소요됩니다.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/app')}
              className="px-6 py-3 rounded-xl text-sm font-bold text-[#2E7D6B] bg-[#E8F5E9] whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity"
            >
              대시보드로
            </button>
            <button
              onClick={() => {
                setIssueDone(false);
                setIssueForm(initialIssueForm);
                setIssueAgreed(false);
              }}
              className="px-6 py-3 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              추가 요청하기
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
          <div className="px-5 py-4 flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <i className="ri-arrow-left-line text-gray-500" />
            </button>
            <div>
              <h1 className="text-base font-extrabold text-gray-900">보험청구 가이드</h1>
              <p className="text-[11px] text-gray-400">4단계 프로세스로 알아보는 보험청구</p>
            </div>
          </div>
        </div>

        <div className="px-5 pt-4 pb-24 space-y-4">

          {/* Overview card */}
          <div className="rounded-2xl p-5" style={{ background: 'linear-gradient(135deg, #0d2b24 0%, #1a4a3a 100%)' }}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(111,207,184,0.2)' }}>
                <i className="ri-guide-line text-sm" style={{ color: '#6fcfb8' }} />
              </div>
              <p className="text-sm font-bold text-white">보험청구 4단계 프로세스</p>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {['사전확인', '간병일지', '정산', '청구'].map((label, i) => (
                <div key={i} className="text-center">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-1" style={{ backgroundColor: 'rgba(111,207,184,0.25)' }}>
                    <span className="text-xs font-black" style={{ color: '#6fcfb8' }}>{i + 1}</span>
                  </div>
                  <p className="text-[10px] font-medium text-white/70">{label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Step cards */}
          {steps.map((step, idx) => {
            const isExpanded = expandedStep === idx;
            return (
              <div
                key={idx}
                className="rounded-2xl border bg-white overflow-hidden transition-all"
                style={{ borderColor: isExpanded ? '#2E7D6B' : 'rgba(46,125,107,0.1)' }}
              >
                <button
                  onClick={() => setExpandedStep(isExpanded ? null : idx)}
                  className="w-full flex items-center gap-3 p-5 text-left cursor-pointer hover:bg-gray-50/50 transition-colors"
                >
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: isExpanded ? '#2E7D6B' : 'rgba(46,125,107,0.1)' }}
                  >
                    <span
                      className="text-sm font-black"
                      style={{ color: isExpanded ? '#fff' : '#2E7D6B' }}
                    >
                      {idx + 1}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] font-bold tracking-widest uppercase" style={{ color: '#2E7D6B' }}>{step.phase}</span>
                      <span className="text-[10px] text-gray-400">{step.timing}</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{step.title}</p>
                  </div>
                  <i
                    className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-gray-400 text-lg transition-transform`}
                  />
                </button>

                {isExpanded && (
                  <div className="px-5 pb-5 space-y-3">
                    <p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p>
                    <div className="space-y-2">
                      {step.items.map((item, iidx) => (
                        <div key={iidx} className="flex items-start gap-2.5">
                          <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: 'rgba(46,125,107,0.1)' }}>
                            <i className="ri-check-line text-xs" style={{ color: '#2E7D6B' }} />
                          </div>
                          <span className="text-xs text-gray-700 leading-relaxed">{item}</span>
                        </div>
                      ))}
                    </div>
                    {step.warning && (
                      <div className="rounded-xl px-3 py-2.5 flex items-start gap-2" style={{ backgroundColor: 'rgba(255,193,7,0.06)', border: '1px solid rgba(255,193,7,0.2)' }}>
                        <i className="ri-alert-line text-sm mt-0.5 flex-shrink-0" style={{ color: '#b8860b' }} />
                        <p className="text-[11px] text-gray-600 leading-relaxed">{step.warning}</p>
                      </div>
                    )}
                    {idx === 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDownload(!showDownload);
                        }}
                        className="w-full mt-2 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 cursor-pointer hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: '#f0f9f6', color: '#2E7D6B', border: '1px solid rgba(46,125,107,0.2)' }}
                      >
                        <i className="ri-download-2-line text-sm" />
                        간병일지 양식 다운로드 바로가기
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* 간병일지 다운로드 섹션 */}
          {showDownload && (
            <div className="rounded-2xl border bg-white p-5" style={{ borderColor: 'rgba(46,125,107,0.12)' }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(46,125,107,0.1)' }}>
                  <i className="ri-download-2-line text-sm" style={{ color: '#2E7D6B' }} />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-gray-900">간병일지 양식 다운로드</h3>
                  <p className="text-[10px] text-gray-400">코리아케어러스 공식 양식</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed mb-4">
                간병인은 코리아케어러스 공식 양식으로 <strong className="text-gray-700">매일 간병일지를 작성</strong>해야 합니다. 보험사별 양식이 상이할 수 있으니 당사로 문의 부탁드립니다.
              </p>
              <div className="flex flex-col gap-2.5">
                <a
                  href="https://storage.readdy-site.link/project_files/85651164-9881-44bb-8cd7-002bfdcb405b/5c697202-8ad9-4e34-ac08-218afddde98f_-_.jpg?v=070242f12d6341e7692f61bf2fab374f"
                  download="간병일지_코리아케어러스.jpg"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#2E7D6B' }}
                >
                  <i className="ri-image-line" />간병일지 양식 (JPG) 다운로드
                </a>
                <a
                  href="https://storage.readdy-site.link/project_files/85651164-9881-44bb-8cd7-002bfdcb405b/4c16399f-393b-4b34-8cba-ab9c90f2097a_-_.pdf?v=39a1b7db47ac57a7935e46154017abc8"
                  download="간병일지_코리아케어러스.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#f0f9f6', color: '#2E7D6B', border: '1px solid rgba(46,125,107,0.3)' }}
                >
                  <i className="ri-file-pdf-line" />간병일지 양식 (PDF) 다운로드
                </a>
              </div>
              <p className="text-[10px] text-gray-400 mt-3">
                다운로드 후 출력하여 간병 기간 동안 매일 작성하세요. 작성이 어려우시면 고객센터(1566-8654)로 문의 바랍니다.
              </p>
            </div>
          )}

          {/* ===== CLICKABLE Document flow summary ===== */}
          <div className="rounded-2xl border bg-white p-5" style={{ borderColor: 'rgba(46,125,107,0.1)' }}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(46,125,107,0.1)' }}>
                <i className="ri-file-list-3-line text-sm" style={{ color: '#2E7D6B' }} />
              </div>
              <h3 className="text-sm font-extrabold text-gray-900">서류 흐름 요약</h3>
              <span className="text-[10px] text-gray-400 ml-auto">클릭하여 이동</span>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => { setShowSubmitForm(true); scrollToSection(submitSectionRef); }}
                className="w-full flex items-center gap-3 p-3 rounded-xl text-left cursor-pointer hover:opacity-90 transition-all active:scale-[0.98]"
                style={{ backgroundColor: '#f0f9f6' }}
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#2E7D6B' }}>
                  <span className="text-xs font-black text-white">4</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-gray-900">환자 → 회사 제출서류</p>
                  <p className="text-[10px] text-gray-500">간호기록지·입퇴원확인서·간병일지·이체확인증</p>
                </div>
                <i className="ri-arrow-right-s-line text-gray-400" />
              </button>
              <div className="flex justify-center">
                <i className="ri-arrow-down-line text-lg" style={{ color: '#2E7D6B' }} />
              </div>
              <button
                type="button"
                onClick={() => { setShowIssueForm(true); scrollToSection(issueSectionRef); }}
                className="w-full flex items-center gap-3 p-3 rounded-xl text-left cursor-pointer hover:opacity-90 transition-all active:scale-[0.98]"
                style={{ backgroundColor: '#f0f9f6' }}
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#2E7D6B' }}>
                  <span className="text-xs font-black text-white">3</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-gray-900">회사 → 환자 발급서류</p>
                  <p className="text-[10px] text-gray-500">이용내역확인서·회원가입확인서·사업자등록증</p>
                </div>
                <i className="ri-arrow-right-s-line text-gray-400" />
              </button>
              <div className="flex justify-center">
                <i className="ri-arrow-down-line text-lg" style={{ color: '#2E7D6B' }} />
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: '#f0f9f6' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1a6b5a' }}>
                  <span className="text-xs font-black text-white">7</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-gray-900">보험사 최종 제출</p>
                  <p className="text-[10px] text-gray-500">7종 전체 서류를 보험사에 제출</p>
                </div>
              </div>
            </div>
          </div>

          {/* ============ SECTION A: 제출서류 첨부 및 제출 ============ */}
          <div ref={submitSectionRef} className="rounded-2xl border bg-white overflow-hidden" style={{ borderColor: 'rgba(46,125,107,0.12)' }}>
            {/* Section Header */}
            <div className="px-5 pt-5 pb-2 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(46,125,107,0.1)' }}>
                <i className="ri-upload-cloud-2-line text-sm" style={{ color: '#2E7D6B' }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-extrabold text-gray-900">제출서류 첨부 및 제출</h3>
                <p className="text-[10px] text-gray-400">간호기록지·입퇴원확인서·간병일지·이체확인증</p>
              </div>
              <span className="text-[10px] font-bold px-2.5 py-1 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgba(46,125,107,0.1)', color: '#2E7D6B' }}>
                환자 → 회사
              </span>
            </div>

            {/* CLICKABLE file cards */}
            <div className="px-5 pb-2 space-y-2">
              <p className="text-[11px] text-gray-500">각 서류를 클릭하여 파일을 첨부하세요</p>
              {docFileLabels.map((doc) => {
                const hasFile = !!files[doc.key].file;
                const isActive = activeFileCard === doc.key;
                return (
                  <button
                    key={doc.key}
                    type="button"
                    onClick={() => {
                      if (hasFile) {
                        // If already has file, show confirmation to change
                        setActiveFileCard(activeFileCard === doc.key ? null : doc.key);
                      } else {
                        fileInputRefs.current[doc.key]?.click();
                      }
                    }}
                    className={`w-full rounded-xl border-2 p-3.5 text-left cursor-pointer transition-all duration-200 ${
                      hasFile
                        ? 'border-[#2E7D6B] bg-[#f0f9f6]'
                        : isActive
                          ? 'border-[#2E7D6B] bg-[#fafffe]'
                          : 'border-dashed border-gray-200 bg-[#fafafa] hover:border-[rgba(46,125,107,0.3)] hover:bg-[#fafffe]'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${hasFile ? 'scale-110' : ''}`} style={{ backgroundColor: hasFile ? '#2E7D6B' : 'rgba(46,125,107,0.1)' }}>
                        {hasFile ? (
                          <i className="ri-check-line text-sm text-white" />
                        ) : (
                          <i className={`${doc.icon} text-sm`} style={{ color: '#2E7D6B' }} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs font-bold text-gray-900">{doc.label}</p>
                          <span className="text-[9px] text-gray-400">{doc.source}</span>
                        </div>
                        {hasFile && (
                          <p className="text-[10px] font-medium mt-0.5 truncate" style={{ color: '#2E7D6B' }}>
                            {files[doc.key].file!.name}
                          </p>
                        )}
                        {!hasFile && (
                          <p className="text-[10px] text-gray-400 mt-0.5">클릭하여 파일 첨부</p>
                        )}
                      </div>
                      <input
                        ref={(el) => { fileInputRefs.current[doc.key] = el; }}
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf,.gif,.bmp"
                        onChange={(e) => handleFileChange(doc.key, e)}
                        className="hidden"
                      />
                      {hasFile ? (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span
                            className="text-[10px] font-bold px-2 py-1 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                            style={{ backgroundColor: '#fff', color: '#2E7D6B', border: '1px solid #2E7D6B' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              fileInputRefs.current[doc.key]?.click();
                            }}
                          >
                            변경
                          </span>
                          <span
                            className="w-6 h-6 rounded-md flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                            style={{ backgroundColor: 'rgba(239,68,68,0.08)' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFiles((prev) => ({ ...prev, [doc.key]: { ...prev[doc.key], file: null } }));
                            }}
                          >
                            <i className="ri-close-line text-xs" style={{ color: '#ef4444' }} />
                          </span>
                        </div>
                      ) : (
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(46,125,107,0.08)' }}>
                          <i className="ri-add-line text-sm" style={{ color: '#2E7D6B' }} />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
              {uploadedCount > 0 && (
                <p className="text-[10px] text-gray-500 text-center pt-1">
                  <span style={{ color: '#2E7D6B', fontWeight: 700 }}>{uploadedCount}개</span> 파일 첨부 완료 — 아래 버튼으로 제출하세요
                </p>
              )}
            </div>

            {/* Toggle button */}
            <div className="px-5 pb-2 pt-1">
              <button
                onClick={() => setShowSubmitForm(!showSubmitForm)}
                className="w-full py-3 rounded-xl text-sm font-bold whitespace-nowrap cursor-pointer hover:opacity-90 transition-all flex items-center justify-center gap-2"
                style={{
                  backgroundColor: showSubmitForm ? '#f0f9f6' : '#2E7D6B',
                  color: showSubmitForm ? '#2E7D6B' : '#fff',
                  border: showSubmitForm ? '1px solid #2E7D6B' : 'none',
                }}
              >
                <i className={`${showSubmitForm ? 'ri-upload-cloud-line' : 'ri-upload-cloud-fill'} text-base`} />
                {showSubmitForm ? '정보 입력 폼 닫기' : '정보 입력하고 제출하기'}
              </button>
            </div>

            {showSubmitForm && (
              <form onSubmit={handleSubmitForm} data-readdy-form="" className="px-5 pb-5 space-y-4">
                {/* 신청인 정보 */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(46,125,107,0.1)' }}>
                      <i className="ri-user-smile-line text-xs" style={{ color: '#2E7D6B' }} />
                    </div>
                    <h4 className="text-xs font-extrabold text-gray-900">신청인 정보</h4>
                  </div>
                  <div className="space-y-2.5">
                    <div>
                      <label className={labelClass}>성명 <span className="text-red-400">*</span></label>
                      <input type="text" name="requester_name" value={submitForm.requester_name} onChange={handleSubmitChange} placeholder="홍길동" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>연락처 <span className="text-red-400">*</span></label>
                      <input type="text" name="requester_phone" value={submitForm.requester_phone} onChange={handleSubmitChange} placeholder="010-0000-0000" className={inputClass} />
                    </div>
                  </div>
                </div>

                {/* 환자 정보 */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(245,124,0,0.1)' }}>
                      <i className="ri-heart-pulse-line text-xs" style={{ color: '#F57C00' }} />
                    </div>
                    <h4 className="text-xs font-extrabold text-gray-900">환자 정보</h4>
                  </div>
                  <div className="space-y-2.5">
                    <div>
                      <label className={labelClass}>환자 성명 <span className="text-red-400">*</span></label>
                      <input type="text" name="patient_name" value={submitForm.patient_name} onChange={handleSubmitChange} placeholder="홍길순" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>환자 생년월일 (6자리) <span className="text-red-400">*</span></label>
                      <input type="text" name="patient_birth" value={submitForm.patient_birth} onChange={handleSubmitChange} placeholder="500101" maxLength={6} className={inputClass} />
                    </div>
                  </div>
                </div>

                {/* 보험 정보 */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(123,31,162,0.1)' }}>
                      <i className="ri-shield-check-line text-xs" style={{ color: '#7B1FA2' }} />
                    </div>
                    <h4 className="text-xs font-extrabold text-gray-900">보험 정보</h4>
                  </div>
                  <div>
                    <label className={labelClass}>보험사 <span className="text-red-400">*</span></label>
                    <select name="insurance_company" value={submitForm.insurance_company} onChange={handleSubmitChange} className={inputClass}>
                      <option value="">선택하세요</option>
                      <option value="삼성생명">삼성생명</option>
                      <option value="한화생명">한화생명</option>
                      <option value="교보생명">교보생명</option>
                      <option value="삼성화재">삼성화재</option>
                      <option value="현대해상">현대해상</option>
                      <option value="DB손해보험">DB손해보험</option>
                      <option value="KB손해보험">KB손해보험</option>
                      <option value="메리츠화재">메리츠화재</option>
                      <option value="흥국생명">흥국생명</option>
                      <option value="NH농협생명">NH농협생명</option>
                      <option value="기타">기타</option>
                    </select>
                  </div>
                </div>

                {/* 추가 요청사항 */}
                <div>
                  <label className={labelClass}>추가 요청사항</label>
                  <textarea
                    name="notes"
                    value={submitForm.notes}
                    onChange={handleSubmitChange}
                    placeholder="추가로 필요한 서류나 특이사항이 있으면 적어주세요"
                    maxLength={500}
                    rows={3}
                    className={`${inputClass} resize-none`}
                  />
                  <p className="text-[10px] text-gray-400 mt-1 text-right">{submitForm.notes.length}/500</p>
                </div>

                {/* 개인정보 동의 */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={submitAgreed}
                    onChange={(e) => setSubmitAgreed(e.target.checked)}
                    className="w-5 h-5 rounded-md border-gray-300 text-[#2E7D6B] focus:ring-[#2E7D6B] mt-0.5 shrink-0 cursor-pointer"
                  />
                  <span className="text-xs text-gray-500 leading-relaxed">
                    개인정보 수집 및 이용에 동의합니다. 수집된 정보는 보험서류 제출 목적으로만 사용되며, 서비스 종료 후 안전하게 파기됩니다.
                  </span>
                </label>

                {submitError && (
                  <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-100 rounded-xl">
                    <i className="ri-error-warning-line text-red-400 text-sm" />
                    <p className="text-sm text-red-600">{submitError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitLoading}
                  className="w-full py-4 rounded-2xl text-base font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#2E7D6B' }}
                >
                  {submitLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />제출 중...
                    </span>
                  ) : (
                    '서류 첨부하고 제출하기'
                  )}
                </button>

                <p className="text-[10px] text-gray-400 text-center">
                  요청 후 담당자가 확인하여 빠른 시일 내에 연락드리겠습니다
                </p>
              </form>
            )}
          </div>

          {/* ============ SECTION B: 당사 서류 발급 요청 ============ */}
          <div ref={issueSectionRef} className="rounded-2xl border bg-white overflow-hidden" style={{ borderColor: 'rgba(26,107,90,0.12)' }}>
            <div className="px-5 pt-5 pb-2 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(26,107,90,0.1)' }}>
                <i className="ri-file-copy-2-line text-sm" style={{ color: '#1a6b5a' }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-extrabold text-gray-900">당사 서류 발급 요청</h3>
                <p className="text-[10px] text-gray-400">이용내역확인서·회원가입확인서·사업자등록증</p>
              </div>
              <span className="text-[10px] font-bold px-2.5 py-1 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgba(26,107,90,0.1)', color: '#1a6b5a' }}>
                회사 → 환자
              </span>
            </div>

            <div className="px-5 pb-2">
              <p className="text-[11px] text-gray-500 leading-relaxed">
                4종 서류를 회사에 제출하시면, 당사에서 아래 3종 서류를 발급해 드립니다.
              </p>
            </div>

            {/* CLICKABLE company doc cards */}
            <div className="px-5 pb-3 flex flex-col gap-2">
              {[
                { key: 'usage_cert', num: '01', title: '간병인매칭서비스 이용내역 확인서', desc: '코리아케어러스를 통한 간병서비스 이용 내역 증명' },
                { key: 'member_cert', num: '02', title: '회원가입 사실확인서', desc: '환자(또는 가족)의 당사 회원가입 사실 증명' },
                { key: 'biz_cert', num: '03', title: '코리아케어러스 사업자등록증', desc: '당사의 정식 사업자 등록 증명' },
              ].map((doc) => (
                <button
                  key={doc.key}
                  type="button"
                  onClick={() => { setShowIssueForm(true); scrollToSection(issueSectionRef); }}
                  className="flex items-center gap-2.5 py-2.5 px-3 rounded-xl text-left cursor-pointer hover:opacity-90 transition-all active:scale-[0.98]"
                  style={{ backgroundColor: '#f8fbfa' }}
                >
                  <span className="text-[10px] font-black flex-shrink-0" style={{ color: '#1a6b5a' }}>{doc.num}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold text-gray-900">{doc.title}</p>
                    <p className="text-[10px] text-gray-400">{doc.desc}</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>

            {/* Toggle button */}
            <div className="px-5 pb-2">
              <button
                onClick={() => setShowIssueForm(!showIssueForm)}
                className="w-full py-3 rounded-xl text-sm font-bold whitespace-nowrap cursor-pointer hover:opacity-90 transition-all flex items-center justify-center gap-2"
                style={{
                  backgroundColor: showIssueForm ? '#f4faf8' : '#1a6b5a',
                  color: showIssueForm ? '#1a6b5a' : '#fff',
                  border: showIssueForm ? '1px solid #1a6b5a' : 'none',
                }}
              >
                <i className={`${showIssueForm ? 'ri-file-copy-line' : 'ri-file-copy-fill'} text-base`} />
                {showIssueForm ? '요청 폼 닫기' : '서류 발급 요청하기'}
              </button>
            </div>

            {showIssueForm && (
              <form onSubmit={handleIssueForm} data-readdy-form="" className="px-5 pb-5 space-y-4">
                <div className="rounded-xl p-3 flex items-start gap-2.5" style={{ backgroundColor: 'rgba(26,107,90,0.04)', border: '1px solid rgba(26,107,90,0.1)' }}>
                  <i className="ri-information-line text-sm flex-shrink-0 mt-0.5" style={{ color: '#1a6b5a' }} />
                  <div>
                    <p className="text-[11px] font-bold text-gray-900 mb-0.5">발급 요청 전 확인사항</p>
                    <ul className="text-[10px] text-gray-500 space-y-1">
                      <li>· 4종 서류를 먼저 제출하셨나요?</li>
                      <li>· 서류 제출은 위 '제출서류 첨부 및 제출' 섹션을 이용해주세요.</li>
                      <li>· 발급까지 영업일 기준 2~3일 소요됩니다.</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(26,107,90,0.1)' }}>
                      <i className="ri-user-smile-line text-xs" style={{ color: '#1a6b5a' }} />
                    </div>
                    <h4 className="text-xs font-extrabold text-gray-900">신청인 정보</h4>
                  </div>
                  <div className="space-y-2.5">
                    <div>
                      <label className={labelClass}>성명 <span className="text-red-400">*</span></label>
                      <input type="text" name="requester_name" value={issueForm.requester_name} onChange={handleIssueChange} placeholder="홍길동" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>연락처 <span className="text-red-400">*</span></label>
                      <input type="text" name="requester_phone" value={issueForm.requester_phone} onChange={handleIssueChange} placeholder="010-0000-0000" className={inputClass} />
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(245,124,0,0.1)' }}>
                      <i className="ri-heart-pulse-line text-xs" style={{ color: '#F57C00' }} />
                    </div>
                    <h4 className="text-xs font-extrabold text-gray-900">환자 정보</h4>
                  </div>
                  <div className="space-y-2.5">
                    <div>
                      <label className={labelClass}>환자 성명 <span className="text-red-400">*</span></label>
                      <input type="text" name="patient_name" value={issueForm.patient_name} onChange={handleIssueChange} placeholder="홍길순" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>환자 생년월일 (6자리) <span className="text-red-400">*</span></label>
                      <input type="text" name="patient_birth" value={issueForm.patient_birth} onChange={handleIssueChange} placeholder="500101" maxLength={6} className={inputClass} />
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(46,125,107,0.1)' }}>
                      <i className="ri-calendar-line text-xs" style={{ color: '#2E7D6B' }} />
                    </div>
                    <h4 className="text-xs font-extrabold text-gray-900">간병 기간</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-2.5">
                    <div>
                      <label className={labelClass}>시작일</label>
                      <input type="date" name="care_start_date" value={issueForm.care_start_date} onChange={handleIssueChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>종료일</label>
                      <input type="date" name="care_end_date" value={issueForm.care_end_date} onChange={handleIssueChange} className={inputClass} />
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(123,31,162,0.1)' }}>
                      <i className="ri-shield-check-line text-xs" style={{ color: '#7B1FA2' }} />
                    </div>
                    <h4 className="text-xs font-extrabold text-gray-900">보험 정보</h4>
                  </div>
                  <div>
                    <label className={labelClass}>보험사 <span className="text-red-400">*</span></label>
                    <select name="insurance_company" value={issueForm.insurance_company} onChange={handleIssueChange} className={inputClass}>
                      <option value="">선택하세요</option>
                      <option value="삼성생명">삼성생명</option>
                      <option value="한화생명">한화생명</option>
                      <option value="교보생명">교보생명</option>
                      <option value="삼성화재">삼성화재</option>
                      <option value="현대해상">현대해상</option>
                      <option value="DB손해보험">DB손해보험</option>
                      <option value="KB손해보험">KB손해보험</option>
                      <option value="메리츠화재">메리츠화재</option>
                      <option value="흥국생명">흥국생명</option>
                      <option value="NH농협생명">NH농협생명</option>
                      <option value="기타">기타</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className={labelClass}>추가 요청사항</label>
                  <textarea
                    name="notes"
                    value={issueForm.notes}
                    onChange={handleIssueChange}
                    placeholder="발급 관련 특이사항이나 추가 요청이 있으면 적어주세요"
                    maxLength={500}
                    rows={3}
                    className={`${inputClass} resize-none`}
                  />
                  <p className="text-[10px] text-gray-400 mt-1 text-right">{issueForm.notes.length}/500</p>
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={issueAgreed}
                    onChange={(e) => setIssueAgreed(e.target.checked)}
                    className="w-5 h-5 rounded-md border-gray-300 text-[#1a6b5a] focus:ring-[#1a6b5a] mt-0.5 shrink-0 cursor-pointer"
                  />
                  <span className="text-xs text-gray-500 leading-relaxed">
                    개인정보 수집 및 이용에 동의합니다. 수집된 정보는 서류 발급 목적으로만 사용되며, 서비스 종료 후 안전하게 파기됩니다.
                  </span>
                </label>

                {issueError && (
                  <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-100 rounded-xl">
                    <i className="ri-error-warning-line text-red-400 text-sm" />
                    <p className="text-sm text-red-600">{issueError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={issueLoading}
                  className="w-full py-4 rounded-2xl text-base font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#1a6b5a' }}
                >
                  {issueLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />요청 중...
                    </span>
                  ) : (
                    '서류 발급 요청하기'
                  )}
                </button>

                <p className="text-[10px] text-gray-400 text-center">
                  요청 후 영업일 기준 2~3일 내에 서류가 발급됩니다
                </p>
              </form>
            )}
          </div>

        </div>
      </div>
    </AppLayout>
  );
}